import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import CollapsibleCard from '@/components/ui/CollapsibleCard';
import Button from '@/components/ui/Button';
import AnalyticsView from '@/components/analytics/AnalyticsView';
import {
  akeys, fetchAnalyticsMe, fetchCreativeAds, fetchUnlinkedAds, fmtMoney, linkAd, PLATFORM_LABEL, unlinkAd,
} from '@/lib/analytics';

/** The Analytics fold on a piece or a strategy: only its own numbers. Renders nothing for
 *  anyone the server would refuse (the summary answers 404 to them anyway). */
export default function AnalyticsSection({ scope, id, clientId, postedSince }: {
  scope: 'creative' | 'strategy'; id: string; clientId?: string; postedSince?: string | null;
}) {
  const me = useQuery({ queryKey: akeys.me, queryFn: fetchAnalyticsMe, staleTime: 5 * 60_000 });
  if (!me.data?.can_view) return null;
  return (
    <CollapsibleCard className="mb-5" title="Analytics" storageKey={`${scope}.analytics.open`}>
      <AnalyticsView scope={scope} id={id} postedSince={postedSince} compact />
      {scope === 'creative' && me.data.audience === 'admin' && clientId && <LinkedAds creativeId={id} clientId={clientId} />}
    </CollapsibleCard>
  );
}

/** The ads that ran this piece. Found by the piece code in the ad name; others linked here. */
function LinkedAds({ creativeId, clientId }: { creativeId: string; clientId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const ads = useQuery({ queryKey: akeys.ads(creativeId), queryFn: () => fetchCreativeAds(creativeId) });
  const free = useQuery({ queryKey: akeys.unlinked(clientId), queryFn: () => fetchUnlinkedAds(clientId), enabled: adding });
  const done = () => { qc.invalidateQueries({ queryKey: ['analytics'] }); };
  const link = useMutation({
    mutationFn: (a: { platform: string; ad_id: string }) => linkAd(creativeId, a.platform, a.ad_id),
    onSuccess: () => { done(); toast.success('Ad linked'); },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Could not link the ad'),
  });
  const unlink = useMutation({
    mutationFn: (a: { platform: string; ad_id: string }) => unlinkAd(creativeId, a.platform, a.ad_id),
    onSuccess: () => { done(); toast.success('Ad unlinked'); },
    onError: () => toast.error('Could not unlink the ad'),
  });
  return (
    <div className="mt-4 border-t border-line pt-4" data-testid="analytics-linked-ads">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-sm font-semibold text-muted-strong">Ads that ran this piece</h3>
        <Button size="sm" variant="ghost" onClick={() => setAdding((x) => !x)}>{adding ? 'Close' : 'Link an ad'}</Button>
      </div>
      {!ads.data?.length ? (
        <p className="text-xs text-muted">No ad linked yet. An ad whose name holds this piece's code links itself at the next sync.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {ads.data.map((a) => (
            <li key={a.platform + a.ad_id} className="flex items-center justify-between gap-3 text-xs rounded-lg border border-line px-3 py-2">
              <span className="min-w-0"><span className="text-muted">{PLATFORM_LABEL[a.platform] ?? a.platform} · </span>
                <span className="text-muted-strong">{a.ad_name ?? a.ad_id}</span>
                <span className="text-muted"> · {a.campaign_name ?? '-'} · {a.linked_by === 'manual' ? 'linked by hand' : 'by its name'}</span></span>
              {a.linked_by === 'manual' && (
                <button type="button" className="text-red-400 hover:underline shrink-0" disabled={unlink.isPending}
                  onClick={() => { if (confirm('Unlink this ad from the piece?')) unlink.mutate(a); }}>Unlink</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && (
        <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-line">
          {free.isLoading && <p className="text-xs text-muted p-3">Loading the clinic's ads...</p>}
          {free.data && !free.data.length && <p className="text-xs text-muted p-3">Every ad of this clinic is already linked.</p>}
          {free.data?.map((a) => (
            <div key={a.platform + a.ad_id} className="flex items-center justify-between gap-3 text-xs px-3 py-2 border-t border-line first:border-t-0">
              <span className="min-w-0 truncate"><span className="text-muted">{PLATFORM_LABEL[a.platform] ?? a.platform} · </span>
                {a.ad_name ?? a.ad_id}<span className="text-muted"> · {fmtMoney(a.spend)}</span></span>
              <Button size="sm" variant="secondary" loading={link.isPending && link.variables?.ad_id === a.ad_id}
                onClick={() => link.mutate(a)}>Link</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
